import { useEditMode } from '../../contexts/EditModeContext';
import { useVariableContext } from '../../hooks/useVariableContext';
import { EditableBlockWrapper } from '../EditableBlockWrapper';
import { HeroBlock } from './HeroBlock';
import { TextBlock } from './TextBlock';
import { RichTextBlock } from './RichTextBlock';
import { ImageBlock } from './ImageBlock';
import { BannerBlock } from './BannerBlock';
import { CardGridBlock } from './CardGridBlock';
import { SpacerBlock } from './SpacerBlock';
import { HtmlBlock } from './HtmlBlock';
import { CollectionBlock } from './CollectionBlock';
import { CollectionEntriesGridBlock } from '../../blocks/CollectionEntriesGridBlock';
import { LockedContentBlock } from './LockedContentBlock';
import { CustomBlock } from './CustomBlock';
import { FeatureGridBlock } from './FeatureGridBlock';
import { TestimonialBlock } from './TestimonialBlock';
import { PricingTableBlock } from './PricingTableBlock';
import { FormBlock } from './FormBlock';
import { VideoBlock } from './VideoBlock';
import { AccordionBlock } from './AccordionBlock';
import { CountdownBlock } from './CountdownBlock';
import { RowBlock } from './RowBlock';
import { LoyaltyHeroBlock } from './loyalty/LoyaltyHeroBlock';
import { LoyaltyTierProgressBlock } from './loyalty/LoyaltyTierProgressBlock';
import { LoyaltyQuickActionsBlock } from './loyalty/LoyaltyQuickActionsBlock';
import { LoyaltyActivityFeedBlock } from './loyalty/LoyaltyActivityFeedBlock';
import { LoyaltyBadgesGridBlock } from './loyalty/LoyaltyBadgesGridBlock';
import { LoyaltyMissionsBlock } from './loyalty/LoyaltyMissionsBlock';
import { LoyaltyGameArenaBlock } from './loyalty/LoyaltyGameArenaBlock';
import { LoyaltyFlashRewardBlock } from './loyalty/LoyaltyFlashRewardBlock';
import { LoyaltyLeaderboardFullBlock } from './loyalty/LoyaltyLeaderboardFullBlock';
import { LoyaltyContestsBlock } from './loyalty/LoyaltyContestsBlock';
import { LoyaltyContestBlock } from './loyalty/LoyaltyContestBlock';
import { LoyaltyFriendStandingsBlock } from './loyalty/LoyaltyFriendStandingsBlock';
import { LoyaltyInviteFriendsBlock } from './loyalty/LoyaltyInviteFriendsBlock';
import { LoyaltyBottomNavBlock } from './loyalty/LoyaltyBottomNavBlock';
import { LoyaltySidebarNavBlock } from './loyalty/LoyaltySidebarNavBlock';
import { LoyaltyRewardsBlock } from './loyalty/LoyaltyRewardsBlock';
import { LoyaltyPointsBlock } from './loyalty/LoyaltyPointsBlock';
import { LoyaltyProfileBlock } from './loyalty/LoyaltyProfileBlock';
import { LoyaltyCouponsBlock } from './loyalty/LoyaltyCouponsBlock';
import { LoyaltyRedemptionHistoryBlock } from './loyalty/LoyaltyRedemptionHistoryBlock';
import { DividerBlock } from './DividerBlock';
import { StatsBlock } from './StatsBlock';
import { LogoCloudBlock } from './LogoCloudBlock';
import { TeamBlock } from './TeamBlock';
import { TimelineBlock } from './TimelineBlock';
import { MapBlock } from './MapBlock';
import { SocialLinksBlock } from './SocialLinksBlock';
import { NewsletterBlock } from './NewsletterBlock';
import { GalleryBlock } from './GalleryBlock';
import { TabsBlock } from './TabsBlock';
import { AlertBlock } from './AlertBlock';
import { BlockquoteBlock } from './BlockquoteBlock';
import { IconBoxesBlock } from './IconBoxesBlock';
import { ProgressBarBlock } from './ProgressBarBlock';
import { DataTableBlock } from './DataTableBlock';
import { ButtonGroupBlock } from './ButtonGroupBlock';
import { CtaBlock } from './CtaBlock';
import { RelatedEntriesBlock } from './RelatedEntriesBlock';
import { PollBlock } from './PollBlock';
import { SurveyBlock } from './SurveyBlock';
import { LoyaltyPollBlock } from './loyalty/LoyaltyPollBlock';
import { LoyaltyQuizBlock } from './loyalty/LoyaltyQuizBlock';

export interface CmsBlock {
  id: string;
  type: string;
  sortOrder: number;
  props: Record<string, unknown>;
  /** True when the block has been replaced with a locked teaser by the server */
  isLocked?: boolean;
  /** Child blocks for row/column containers (populated server-side) */
  children?: CmsBlock[];
}

function renderBlockContent(block: CmsBlock) {
  const p = block.props;

  if (block.isLocked || block.type === 'locked_content') {
    return <LockedContentBlock props={p as Parameters<typeof LockedContentBlock>[0]['props']} />;
  }

  switch (block.type) {
    case 'hero':
      return <HeroBlock props={p as Parameters<typeof HeroBlock>[0]['props']} />;
    case 'text':
      return <TextBlock props={p as Parameters<typeof TextBlock>[0]['props']} />;
    case 'rich_text':
      return <RichTextBlock props={p as Parameters<typeof RichTextBlock>[0]['props']} />;
    case 'image':
      return <ImageBlock props={p as Parameters<typeof ImageBlock>[0]['props']} />;
    case 'banner':
      return <BannerBlock props={p as Parameters<typeof BannerBlock>[0]['props']} />;
    case 'card_grid':
      return <CardGridBlock props={p as Parameters<typeof CardGridBlock>[0]['props']} />;
    case 'spacer':
      return <SpacerBlock props={p as Parameters<typeof SpacerBlock>[0]['props']} />;
    case 'html':
      return <HtmlBlock props={p as Parameters<typeof HtmlBlock>[0]['props']} />;
    case 'collection':
      return <CollectionBlock props={p as Parameters<typeof CollectionBlock>[0]['props']} />;
    case 'collection-entries-grid':
      return (
        <CollectionEntriesGridBlock
          columns={p?.columns as 2 | 3 | 4 | undefined}
          typeSlug={p?.typeSlug as string | undefined}
        />
      );
    case 'block_type':
      return <CustomBlock props={p as Parameters<typeof CustomBlock>[0]['props']} />;
    case 'feature_grid':
      return <FeatureGridBlock props={p as Parameters<typeof FeatureGridBlock>[0]['props']} />;
    case 'testimonial':
      return <TestimonialBlock props={p as Parameters<typeof TestimonialBlock>[0]['props']} />;
    case 'pricing_table':
      return <PricingTableBlock props={p as Parameters<typeof PricingTableBlock>[0]['props']} />;
    case 'form':
      return <FormBlock props={p as Parameters<typeof FormBlock>[0]['props']} />;
    case 'video':
      return <VideoBlock props={p as Parameters<typeof VideoBlock>[0]['props']} />;
    case 'accordion':
      return <AccordionBlock block={block} />;
    case 'countdown':
      return <CountdownBlock props={p as Parameters<typeof CountdownBlock>[0]['props']} />;
    case 'row':
      return <RowBlock block={block} />;
    case 'loyalty_hero':
      return <LoyaltyHeroBlock props={p as Parameters<typeof LoyaltyHeroBlock>[0]['props']} />;
    case 'loyalty_tier_progress':
      return <LoyaltyTierProgressBlock props={p as Parameters<typeof LoyaltyTierProgressBlock>[0]['props']} />;
    case 'loyalty_quick_actions':
      return <LoyaltyQuickActionsBlock props={p as Parameters<typeof LoyaltyQuickActionsBlock>[0]['props']} />;
    case 'loyalty_activity_feed':
      return <LoyaltyActivityFeedBlock props={p as Parameters<typeof LoyaltyActivityFeedBlock>[0]['props']} />;
    case 'loyalty_badges_grid':
      return <LoyaltyBadgesGridBlock props={p as Parameters<typeof LoyaltyBadgesGridBlock>[0]['props']} />;
    case 'loyalty_missions':
      return <LoyaltyMissionsBlock props={p as Parameters<typeof LoyaltyMissionsBlock>[0]['props']} />;
    case 'loyalty_game_arena':
      return <LoyaltyGameArenaBlock props={p as Parameters<typeof LoyaltyGameArenaBlock>[0]['props']} />;
    case 'loyalty_flash_reward':
      return <LoyaltyFlashRewardBlock props={p as Parameters<typeof LoyaltyFlashRewardBlock>[0]['props']} />;
    case 'loyalty_leaderboard_full':
      return <LoyaltyLeaderboardFullBlock props={p as Parameters<typeof LoyaltyLeaderboardFullBlock>[0]['props']} />;
    case 'loyalty_contests':
      return <LoyaltyContestsBlock props={p as Parameters<typeof LoyaltyContestsBlock>[0]['props']} />;
    case 'loyalty_contest':
      return <LoyaltyContestBlock props={p as Parameters<typeof LoyaltyContestBlock>[0]['props']} />;
    case 'loyalty_friend_standings':
      return <LoyaltyFriendStandingsBlock props={p as Parameters<typeof LoyaltyFriendStandingsBlock>[0]['props']} />;
    case 'loyalty_invite_friends':
      return <LoyaltyInviteFriendsBlock props={p as Parameters<typeof LoyaltyInviteFriendsBlock>[0]['props']} />;
    case 'loyalty_bottom_nav':
      return <LoyaltyBottomNavBlock props={p as Parameters<typeof LoyaltyBottomNavBlock>[0]['props']} />;
    case 'loyalty_sidebar_nav':
      return <LoyaltySidebarNavBlock props={p as Parameters<typeof LoyaltySidebarNavBlock>[0]['props']} />;
    case 'loyalty_rewards':
      return <LoyaltyRewardsBlock props={p as Parameters<typeof LoyaltyRewardsBlock>[0]['props']} />;
    case 'loyalty_points':
      return <LoyaltyPointsBlock props={p as Parameters<typeof LoyaltyPointsBlock>[0]['props']} />;
    case 'loyalty_profile':
      return <LoyaltyProfileBlock props={p as Parameters<typeof LoyaltyProfileBlock>[0]['props']} />;
    case 'loyalty_coupons':
      return <LoyaltyCouponsBlock props={p as Parameters<typeof LoyaltyCouponsBlock>[0]['props']} />;
    case 'loyalty_redemption_history':
      return <LoyaltyRedemptionHistoryBlock props={p as Parameters<typeof LoyaltyRedemptionHistoryBlock>[0]['props']} />;
    // ── New blocks ──
    case 'divider':
      return <DividerBlock props={p as Parameters<typeof DividerBlock>[0]['props']} />;
    case 'stats':
      return <StatsBlock props={p as Parameters<typeof StatsBlock>[0]['props']} />;
    case 'logo_cloud':
      return <LogoCloudBlock props={p as Parameters<typeof LogoCloudBlock>[0]['props']} />;
    case 'team':
      return <TeamBlock props={p as Parameters<typeof TeamBlock>[0]['props']} />;
    case 'timeline':
      return <TimelineBlock props={p as Parameters<typeof TimelineBlock>[0]['props']} />;
    case 'map':
      return <MapBlock props={p as Parameters<typeof MapBlock>[0]['props']} />;
    case 'social_links':
      return <SocialLinksBlock props={p as Parameters<typeof SocialLinksBlock>[0]['props']} />;
    case 'newsletter':
      return <NewsletterBlock props={p as Parameters<typeof NewsletterBlock>[0]['props']} />;
    case 'gallery':
      return <GalleryBlock props={p as Parameters<typeof GalleryBlock>[0]['props']} />;
    case 'tabs':
      return <TabsBlock block={block} />;
    case 'alert':
      return <AlertBlock props={p as Parameters<typeof AlertBlock>[0]['props']} />;
    case 'blockquote':
      return <BlockquoteBlock props={p as Parameters<typeof BlockquoteBlock>[0]['props']} />;
    case 'icon_boxes':
      return <IconBoxesBlock props={p as Parameters<typeof IconBoxesBlock>[0]['props']} />;
    case 'progress_bar':
      return <ProgressBarBlock props={p as Parameters<typeof ProgressBarBlock>[0]['props']} />;
    case 'data_table':
      return <DataTableBlock props={p as Parameters<typeof DataTableBlock>[0]['props']} />;
    case 'button_group':
      return <ButtonGroupBlock props={p as Parameters<typeof ButtonGroupBlock>[0]['props']} />;
    case 'cta':
      return <CtaBlock props={p as Parameters<typeof CtaBlock>[0]['props']} />;
    case 'related-entries':
      return <RelatedEntriesBlock props={p as Parameters<typeof RelatedEntriesBlock>[0]['props']} />;
    case 'poll':
      return <PollBlock props={p as Parameters<typeof PollBlock>[0]['props']} />;
    case 'survey':
      return <SurveyBlock props={p as Parameters<typeof SurveyBlock>[0]['props']} />;
    case 'loyalty_poll':
      return <LoyaltyPollBlock props={p as Parameters<typeof LoyaltyPollBlock>[0]['props']} />;
    case 'loyalty_quiz':
      return <LoyaltyQuizBlock props={p as Parameters<typeof LoyaltyQuizBlock>[0]['props']} />;
    default:
      return null;
  }
}

const PADDING_MAP: Record<string, string> = {
  none: '0',
  sm: '0.5rem',
  md: '1.5rem',
  lg: '3rem',
  xl: '5rem',
};

const MAX_WIDTH_MAP: Record<string, string> = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  full: '100%',
};

/** Wraps a block applying styleProps (padding, background, text color, max-width). */
function StyleWrapper({ block, children }: { block: CmsBlock; children: React.ReactNode }) {
  const sp = block.props.styleProps as Record<string, string> | undefined;
  if (!sp || Object.keys(sp).length === 0) return <>{children}</>;

  const style: React.CSSProperties = {};
  if (sp.paddingTop) style.paddingTop = PADDING_MAP[sp.paddingTop] ?? sp.paddingTop;
  if (sp.paddingBottom) style.paddingBottom = PADDING_MAP[sp.paddingBottom] ?? sp.paddingBottom;
  if (sp.backgroundColor) style.backgroundColor = sp.backgroundColor;
  if (sp.textColor) style.color = sp.textColor;

  const maxWidth = sp.maxWidth ? MAX_WIDTH_MAP[sp.maxWidth] : undefined;

  return (
    <div style={style}>
      {maxWidth ? (
        <div style={{ maxWidth, margin: '0 auto' }}>{children}</div>
      ) : (
        children
      )}
    </div>
  );
}

/** Check if a block has meaningful content props (ignoring layout metadata like _col, styleProps) */
function hasContentProps(block: CmsBlock): boolean {
  const METADATA_KEYS = new Set(['_col', 'styleProps']);
  return Object.entries(block.props).some(
    ([k, v]) => !METADATA_KEYS.has(k) && v !== null && v !== undefined && v !== '' && v !== false,
  );
}

export function BlockRenderer({ block }: { block: CmsBlock }) {
  const { isEditMode } = useEditMode();
  const { resolveBlockProps } = useVariableContext();

  // Resolve {{customer.firstName}} etc. in block props (skip in edit mode to keep raw tags visible)
  const resolvedBlock = isEditMode
    ? block
    : { ...block, props: resolveBlockProps(block.props) };

  const content = renderBlockContent(resolvedBlock);

  // In edit mode, show placeholder for blocks with no content (except containers like row)
  const isContainer = block.type === 'row' || block.type === 'accordion' || block.type === 'tabs';
  if (isEditMode && !isContainer && (content === null || !hasContentProps(block))) {
    return (
      <EditableBlockWrapper block={block}>
        <div className="border-2 border-dashed border-indigo-300 bg-indigo-50 rounded-lg px-4 py-8 text-center">
          <div className="text-sm font-semibold text-indigo-500">{block.type.replace(/_/g, ' ')}</div>
          <div className="text-xs text-indigo-400 mt-1">Click to configure this block</div>
        </div>
      </EditableBlockWrapper>
    );
  }

  const wrapped = <StyleWrapper block={block}>{content}</StyleWrapper>;

  if (isEditMode) {
    return <EditableBlockWrapper block={block}>{wrapped}</EditableBlockWrapper>;
  }

  return wrapped;
}
